import React from "react";

import { Image } from "../../components/Image";
import { useMountEffect } from "../../hooks/useMountEffect";
import { IStringField } from "../../models/formFields";
import { FormInputFieldWrapper } from "../FormInputFieldWrapper";
import { FORM_FIELD_INPUT_CLASS_NAME } from "../classNames";
import { ensureCleaveLoaded, getFormatter } from "../formatting";
import { useFormField } from "../hooks/useFormField";

const truncateStringByBytes = (
    original: string,
    maxBytesLimit: number,
): string => {
    let truncated: string;
    let byteLength: number;
    for (let i = original.length; i >= 0; i--) {
        truncated = original.slice(0, i);
        byteLength = encodeURIComponent(truncated.replace(/\d/g, "X")).replace(
            /%[A-F\d]{2,6}/g,
            "U",
        ).length;
        if (byteLength <= maxBytesLimit) {
            return truncated;
        }
    }
    throw Error(
        `This should never happen! Somehow an empty string is longer than ${maxBytesLimit} bytes.`,
    );
};

export const FormInput = <T extends string>(
    props: IStringField<T> & {
        inputMode?: React.InputHTMLAttributes<HTMLInputElement>["inputMode"];
    },
): React.ReactNode => {
    const {
        type = "text",
        minLength,
        maxLength,
        pattern,
        maxBytes,
        formatOptions,
        secureImage,
        inputMode,
    } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    // Load cleave-zen on mount, then register cursor tracker.
    // Dynamic load avoids ESM/CJS cycle during SSR module evaluation.
    useMountEffect(() => {
        if (formatOptions && field.inputRef.current) {
            const inputEl = field.inputRef.current;
            ensureCleaveLoaded().then(() => {
                const formatter = getFormatter(formatOptions);
                formatter.registerCursorTracker(inputEl);
            });
        }
    });

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        if (!event.currentTarget) {
            return;
        }

        // If maxBytes property is provided, check the current value of the input and truncate it to stay within the size limit.
        if (maxBytes) {
            const original = event.currentTarget.value;
            const truncated = truncateStringByBytes(original, maxBytes);
            if (original !== truncated) {
                event.currentTarget.value = truncated;
            }
        }

        // Format value
        if (formatOptions) {
            const formatter = getFormatter(formatOptions);
            const original = event.currentTarget.value;
            const formatted = formatter.format(original);
            if (original !== formatted) {
                event.currentTarget.value = formatted;
            }
        }
    };

    const inputProps = {
        ...field.inputProps,
        minLength,
        maxLength,
        pattern,
        inputMode,
        className: props.className ?? FORM_FIELD_INPUT_CLASS_NAME,
        onChange: (event: React.FormEvent<HTMLInputElement>) => {
            field.handleChange(event, handleChange);
        },
    };

    const image =
        secureImage?.image &&
        secureImage.image.length > 0 &&
        secureImage.alt ? (
            <Image src={secureImage.image} alt={secureImage.alt} lazy={false} />
        ) : null;

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <input ref={field.inputRef} {...inputProps} />
            {image}
        </FormInputFieldWrapper>
    );
};
